import { Modal } from '@lume/ui-components';
import { useQuery } from '@tanstack/react-query';
import { Search, X } from 'lucide-react';
import { useMemo, useState } from 'react';

import { api } from '../../lib/api-client';
import { usePersons } from '../persons/use-persons';

import { useCreateOutboundCampaign } from './hooks/use-outbound-campaigns';

import type { CreateOutboundCampaignRequest } from '../../lib/types';
import type { JSX } from 'react';

interface SelectedTarget {
  personId: string;
  label: string;
}

/**
 * F4-b — criação/ativação de uma campanha (Raiz de Tarefa). Reusa a busca de
 * pessoas existente para materializar a audiência. Neutro: playbook/título/slots
 * são dado do tenant. Ao ativar, a plataforma dispara N conversas-filhas.
 */
export function CampaignCreateModal({
  isOpen,
  onClose,
}: {
  readonly isOpen: boolean;
  readonly onClose: () => void;
}): JSX.Element {
  const { create, isPending } = useCreateOutboundCampaign();
  const [playbookId, setPlaybookId] = useState('');
  const [title, setTitle] = useState('');
  const [slotsRaw, setSlotsRaw] = useState('');
  const [opening, setOpening] = useState('');
  const [mode, setMode] = useState<'H_c' | 'H_a'>('H_c');
  const [quorum, setQuorum] = useState('');
  const [deadlineAt, setDeadlineAt] = useState('');
  const [audience, setAudience] = useState<SelectedTarget[]>([]);
  const [search, setSearch] = useState('');

  const playbooksQ = useQuery({
    queryKey: ['playbooks', 'active-for-campaign'],
    queryFn: () => api.listPlaybooks({ status: 'active' }),
    enabled: isOpen,
    staleTime: 60_000,
  });

  const personsQ = usePersons(search.trim().length >= 2 ? { q: search.trim(), limit: 8 } : {});

  const targetSlots = useMemo(
    () =>
      slotsRaw
        .split(',')
        .map((s) => s.trim())
        .filter((s) => s.length > 0),
    [slotsRaw],
  );

  const canSubmit =
    playbookId.length > 0 && title.trim().length > 0 && audience.length > 0 && !isPending;

  function addTarget(personId: string, label: string): void {
    setAudience((prev) =>
      prev.some((t) => t.personId === personId) ? prev : [...prev, { personId, label }],
    );
    setSearch('');
  }

  function removeTarget(personId: string): void {
    setAudience((prev) => prev.filter((t) => t.personId !== personId));
  }

  function reset(): void {
    setPlaybookId('');
    setTitle('');
    setSlotsRaw('');
    setOpening('');
    setMode('H_c');
    setQuorum('');
    setDeadlineAt('');
    setAudience([]);
    setSearch('');
  }

  async function handleSubmit(): Promise<void> {
    if (!canSubmit) return;
    const closePolicy: CreateOutboundCampaignRequest['closePolicy'] = {};
    if (quorum.trim().length > 0 && Number.isFinite(Number(quorum))) {
      closePolicy.quorum = Number(quorum);
    }
    if (deadlineAt.trim().length > 0) {
      closePolicy.deadlineAt = new Date(deadlineAt).toISOString();
    }
    const body: CreateOutboundCampaignRequest = {
      playbookId,
      title: title.trim(),
      targetSlots,
      audience: audience.map((t) => ({ personId: t.personId })),
      openingConfig: opening.trim().length > 0 ? { text: opening.trim() } : {},
      mode,
      ...(Object.keys(closePolicy).length > 0 && { closePolicy }),
    };
    try {
      await create(body);
      reset();
      onClose();
    } catch {
      // toast já emitido pelo hook; mantém o modal aberto para correção.
    }
  }

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Nova campanha" maxWidth="640px" persistent>
      <div className="flex flex-col gap-4">
        <p className="text-sm text-text-secondary">
          A audiência é materializada na criação — cada alvo vira uma conversa conduzida
          individualmente. Os guards de LGPD/opt-out rodam por-conversa no disparo.
        </p>

        <Field label="Playbook (meta)">
          <select
            className="rounded-md border border-border-subtle bg-surface px-3 py-2 text-sm text-text-primary"
            value={playbookId}
            onChange={(e) => setPlaybookId(e.target.value)}
          >
            <option value="">Selecione…</option>
            {(playbooksQ.data ?? []).map((p) => (
              <option key={p.id} value={p.id}>
                {p.name}
              </option>
            ))}
          </select>
        </Field>

        <Field label="Título">
          <input
            className="rounded-md border border-border-subtle bg-surface px-3 py-2 text-sm text-text-primary"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder="Ex.: Satisfação — visitas da semana"
          />
        </Field>

        <Field label="Audiência">
          <div className="relative">
            <Search
              size={14}
              aria-hidden
              className="absolute left-3 top-1/2 -translate-y-1/2 text-text-secondary"
            />
            <input
              className="w-full rounded-md border border-border-subtle bg-surface py-2 pl-8 pr-3 text-sm text-text-primary"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Buscar pessoas por nome…"
              aria-label="Buscar pessoas"
            />
          </div>
          {search.trim().length >= 2 && (
            <div className="mt-1 max-h-40 overflow-y-auto rounded-md border border-border-subtle bg-surface">
              {personsQ.isLoading ? (
                <p className="px-3 py-2 text-sm text-text-secondary">Buscando…</p>
              ) : (personsQ.data?.data ?? []).length === 0 ? (
                <p className="px-3 py-2 text-sm text-text-secondary">Nenhuma pessoa encontrada.</p>
              ) : (
                (personsQ.data?.data ?? []).map((p) => (
                  <button
                    key={p.id}
                    type="button"
                    className="flex w-full items-center justify-between px-3 py-2 text-left text-sm text-text-primary hover:bg-app-bg"
                    onClick={() => addTarget(p.id, p.displayName)}
                  >
                    <span>{p.displayName}</span>
                    {p.primaryIdentifierValue !== null && (
                      <span className="text-xs text-text-secondary">
                        {p.primaryIdentifierValue}
                      </span>
                    )}
                  </button>
                ))
              )}
            </div>
          )}
          {audience.length > 0 && (
            <div className="mt-2 flex flex-wrap gap-2">
              {audience.map((t) => (
                <span
                  key={t.personId}
                  className="inline-flex items-center gap-1.5 rounded-full border border-border-subtle bg-app-bg px-2.5 py-1 text-xs text-text-primary"
                >
                  {t.label}
                  <button
                    type="button"
                    aria-label={`Remover ${t.label}`}
                    onClick={() => removeTarget(t.personId)}
                    className="text-text-secondary hover:text-text-primary"
                  >
                    <X size={12} />
                  </button>
                </span>
              ))}
            </div>
          )}
          <span className="text-xs text-text-secondary">
            {audience.length} pessoa(s) · a cadência de disparo é limitada pelo worker (~1/min).
          </span>
        </Field>

        <Field label="Slots-alvo (separados por vírgula)">
          <input
            className="rounded-md border border-border-subtle bg-surface px-3 py-2 text-sm text-text-primary"
            value={slotsRaw}
            onChange={(e) => setSlotsRaw(e.target.value)}
            placeholder="satisfacao, retorno"
          />
        </Field>

        <Field label="Abertura (texto)">
          <textarea
            className="rounded-md border border-border-subtle bg-surface px-3 py-2 text-sm text-text-primary"
            rows={2}
            value={opening}
            onChange={(e) => setOpening(e.target.value)}
            placeholder="Mensagem inicial da conversa (vive na config do tenant)"
          />
        </Field>

        <div className="flex gap-4">
          <Field label="Modo">
            <div className="flex gap-2">
              {(['H_c', 'H_a'] as const).map((m) => (
                <button
                  key={m}
                  type="button"
                  onClick={() => setMode(m)}
                  className={`rounded-full border px-3 py-1 text-xs ${
                    mode === m
                      ? 'border-accent bg-[color:var(--brand-subtle)] text-[color:var(--brand-subtle-fg)]'
                      : 'border-border-subtle bg-surface text-text-secondary'
                  }`}
                >
                  {m === 'H_c' ? 'IA conduz (H_c)' : 'Humano (H_a)'}
                </button>
              ))}
            </div>
          </Field>
          <Field label="Quórum (opcional)">
            <input
              type="number"
              min={1}
              className="w-24 rounded-md border border-border-subtle bg-surface px-3 py-2 text-sm text-text-primary"
              value={quorum}
              onChange={(e) => setQuorum(e.target.value)}
            />
          </Field>
          <Field label="Prazo (opcional)">
            <input
              type="datetime-local"
              className="rounded-md border border-border-subtle bg-surface px-3 py-2 text-sm text-text-primary"
              value={deadlineAt}
              onChange={(e) => setDeadlineAt(e.target.value)}
            />
          </Field>
        </div>

        <div className="mt-2 flex items-center gap-2">
          <button
            type="button"
            disabled={!canSubmit}
            onClick={() => void handleSubmit()}
            className="rounded-md bg-accent px-4 py-2 text-sm font-medium text-white disabled:opacity-50"
          >
            {isPending ? 'Ativando…' : 'Ativar campanha'}
          </button>
          <button
            type="button"
            onClick={onClose}
            className="rounded-md border border-border-subtle bg-surface px-4 py-2 text-sm font-medium text-text-secondary"
          >
            Cancelar
          </button>
        </div>
      </div>
    </Modal>
  );
}

function Field({
  label,
  children,
}: {
  readonly label: string;
  readonly children: React.ReactNode;
}): JSX.Element {
  return (
    <label className="flex flex-col gap-1.5">
      <span className="text-sm font-medium text-text-secondary">{label}</span>
      {children}
    </label>
  );
}
